import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Buildings, Plus, Trash, FloppyDisk, X, Pencil, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepartmentsSection({ language }) {
  const isAr = language === 'ar';
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editing, setEditing] = useState({ value: '', label_ar: '', label_en: '', subs: [] });
  const [adding, setAdding] = useState(false);
  const [newDept, setNewDept] = useState({ value: '', label_ar: '', label_en: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/departments`, { withCredentials: true });
      setList(Array.isArray(data) ? data : []);
    } catch {
      toast.error(isAr ? 'فشل تحميل الأقسام' : 'Failed to load departments');
    }
    setLoading(false);
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditing({
      value: d.value || '',
      label_ar: d.label_ar || '',
      label_en: d.label_en || '',
      subs: Array.isArray(d.subs) ? [...d.subs] : [],
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditing({ value: '', label_ar: '', label_en: '', subs: [] }); };

  const saveEdit = async (id) => {
    if (!editing.label_ar.trim() || !editing.label_en.trim()) {
      toast.error(isAr ? 'الاسم بالعربي والإنجليزي مطلوبان' : 'Both AR/EN names required');
      return;
    }
    try {
      await axios.put(`${API}/departments/${id}`, editing, { withCredentials: true });
      toast.success(isAr ? 'تم الحفظ' : 'Saved');
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    }
  };

  const remove = async (d) => {
    const ok = window.confirm(isAr
      ? `هل تريد حذف قسم "${d.label_ar}"؟ سيفشل الحذف إذا كان به موظفون.`
      : `Delete "${d.label_en}"? Will fail if any employee belongs to it.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/departments/${d.id}`, { withCredentials: true });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  const addNew = async () => {
    const v = newDept.value.trim();
    const ar = newDept.label_ar.trim();
    const en = newDept.label_en.trim();
    if (!v || !ar || !en) {
      toast.error(isAr ? 'كل الحقول مطلوبة' : 'All fields required');
      return;
    }
    try {
      await axios.post(`${API}/departments`, { value: v, label_ar: ar, label_en: en }, { withCredentials: true });
      toast.success(isAr ? 'تمت الإضافة' : 'Added');
      setAdding(false);
      setNewDept({ value: '', label_ar: '', label_en: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الإضافة' : 'Add failed'));
    }
  };

  // Sub-departments helpers
  const addSub = () => setEditing(prev => ({ ...prev, subs: [...prev.subs, { value: '', label_ar: '', label_en: '' }] }));
  const updateSub = (idx, key, val) => setEditing(prev => ({
    ...prev,
    subs: prev.subs.map((s, i) => i === idx ? { ...s, [key]: val } : s),
  }));
  const removeSub = (idx) => setEditing(prev => ({ ...prev, subs: prev.subs.filter((_, i) => i !== idx) }));

  return (
    <Card className="border-[#C4BFF0]" data-testid="departments-card">
      <CardHeader>
        <CardTitle className="text-[#655dc6] flex items-center gap-2">
          <Buildings size={22} weight="duotone" />
          {isAr ? 'إدارة الأقسام' : 'Departments Management'}
        </CardTitle>
        <p className="text-xs text-[#655dc6]/60 mt-1">
          {isAr
            ? 'الأقسام والأقسام الفرعية للشركة. يمكنك التعديل/الحذف/الإضافة. الحذف لا يعمل إذا كان القسم مرتبطاً بموظفين.'
            : 'Company departments and sub-departments. Edit, delete, or add. Cannot delete a department that has employees.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-4"><SpinnerGap size={20} className="animate-spin text-[#655dc6]" /></div>
        ) : (
          <>
            {list.map((d) => (
              <div key={d.id} className="rounded-md border border-[#C4BFF0] p-3 bg-white space-y-2" data-testid={`dept-row-${d.value}`}>
                {editingId === d.id ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] text-[#655dc6]/70">{isAr ? 'المعرف (لا تغيره عادة)' : 'Value (key)'}</Label>
                        <Input value={editing.value} onChange={(e) => setEditing(p => ({ ...p, value: e.target.value }))} className="text-xs" dir="rtl" data-testid={`dept-value-${d.value}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#655dc6]/70">{isAr ? 'الاسم بالعربي' : 'Label (AR)'}</Label>
                        <Input value={editing.label_ar} onChange={(e) => setEditing(p => ({ ...p, label_ar: e.target.value }))} className="text-xs" dir="rtl" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#655dc6]/70">{isAr ? 'الاسم بالإنجليزي' : 'Label (EN)'}</Label>
                        <Input value={editing.label_en} onChange={(e) => setEditing(p => ({ ...p, label_en: e.target.value }))} className="text-xs" dir="ltr" />
                      </div>
                    </div>
                    {/* Sub-departments editor */}
                    <div className="pl-3 border-l-2 border-[#E8E6F5] space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#655dc6]/70">{isAr ? 'الأقسام الفرعية' : 'Sub-departments'}</span>
                        <Button size="sm" variant="ghost" onClick={addSub} className="h-6 text-[10px] gap-1"><Plus size={12} />{isAr ? 'إضافة' : 'Add'}</Button>
                      </div>
                      {editing.subs.map((s, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                          <Input className="col-span-3 text-[10px] h-7" placeholder={isAr ? 'المعرف' : 'value'} value={s.value} onChange={(e) => updateSub(i, 'value', e.target.value)} dir="rtl" />
                          <Input className="col-span-4 text-[10px] h-7" placeholder={isAr ? 'الاسم بالعربي' : 'AR'} value={s.label_ar} onChange={(e) => updateSub(i, 'label_ar', e.target.value)} dir="rtl" />
                          <Input className="col-span-4 text-[10px] h-7" placeholder={isAr ? 'الاسم بالإنجليزي' : 'EN'} value={s.label_en} onChange={(e) => updateSub(i, 'label_en', e.target.value)} dir="ltr" />
                          <Button size="sm" variant="ghost" onClick={() => removeSub(i)} className="col-span-1 h-7 w-7 p-0 text-red-500 hover:bg-red-50"><X size={12} /></Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => saveEdit(d.id)} className="bg-[#655dc6] hover:bg-[#544cb0] gap-1 h-7 text-[11px]" data-testid={`save-dept-${d.value}`}>
                        <FloppyDisk size={12} />{isAr ? 'حفظ' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit} className="gap-1 h-7 text-[11px]">
                        <X size={12} />{isAr ? 'إلغاء' : 'Cancel'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-[#2D2A5C]">
                      <span className="font-semibold">{isAr ? d.label_ar : d.label_en}</span>
                      <span className="text-[10px] text-[#655dc6]/50 ms-2">
                        ({isAr ? d.label_en : d.label_ar})
                      </span>
                      {d.subs?.length > 0 && (
                        <span className="text-[10px] text-[#655dc6]/60 block mt-1">
                          {isAr ? 'فرعية: ' : 'Subs: '}
                          {d.subs.map(s => isAr ? s.label_ar : s.label_en).join('، ')}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => startEdit(d)} className="gap-1 h-7 text-[11px]" data-testid={`edit-dept-${d.value}`}>
                        <Pencil size={12} />{isAr ? 'تعديل' : 'Edit'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-red-500 hover:bg-red-50 gap-1 h-7 text-[11px]" data-testid={`delete-dept-${d.value}`}>
                        <Trash size={12} />{isAr ? 'حذف' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add new department */}
            {adding ? (
              <div className="rounded-md border border-dashed border-[#655dc6] p-3 bg-[#F2F0FA] space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input placeholder={isAr ? 'المعرف' : 'value'} value={newDept.value} onChange={(e) => setNewDept(p => ({ ...p, value: e.target.value, label_ar: p.label_ar || e.target.value }))} className="text-xs" dir="rtl" data-testid="new-dept-value" />
                  <Input placeholder={isAr ? 'الاسم بالعربي' : 'AR'} value={newDept.label_ar} onChange={(e) => setNewDept(p => ({ ...p, label_ar: e.target.value }))} className="text-xs" dir="rtl" />
                  <Input placeholder={isAr ? 'الاسم بالإنجليزي' : 'EN'} value={newDept.label_en} onChange={(e) => setNewDept(p => ({ ...p, label_en: e.target.value }))} className="text-xs" dir="ltr" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={addNew} className="bg-[#655dc6] hover:bg-[#544cb0] gap-1 h-7 text-[11px]" data-testid="confirm-add-dept">
                    <FloppyDisk size={12} />{isAr ? 'إضافة' : 'Add'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewDept({ value: '', label_ar: '', label_en: '' }); }} className="gap-1 h-7 text-[11px]">
                    <X size={12} />{isAr ? 'إلغاء' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setAdding(true)} className="border-[#655dc6] text-[#655dc6] gap-2 w-full" data-testid="add-dept-btn">
                <Plus size={16} />{isAr ? 'إضافة قسم جديد' : 'Add new department'}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
